import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { useAuth } from '../../context/AuthContext';

const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';

// ── Confirmation modal ────────────────────────────────────────────────────────

function ConfirmModal({ title, message, detail, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 12, padding: '1.5rem', maxWidth: 440, width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: danger ? '#991b1b' : '#111827' }}>
          {title}
        </h3>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: '#374151' }}>{message}</p>
        {detail && (
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#6b7280' }}>
            {detail}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '7px 16px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, cursor: 'pointer', fontSize: '0.875rem' }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ padding: '7px 16px', background: danger ? '#dc2626' : '#1d4ed8', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Diff breakdown (shown after Compare) ──────────────────────────────────────

function DiffPanel({ diff, tournamentId, onMerge, onPullOverwrite, onPushOverwrite, isBusy, licenseExpired }) {
  if (!diff?.onServer) return null;

  const { events, players, matches } = diff;
  const hasLocalOnly  = events.localOnly.length > 0  || players.localOnly.length > 0;
  const hasServerOnly = events.serverOnly.length > 0 || players.serverOnly.length > 0;
  const canMerge = hasLocalOnly || hasServerOnly;

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.875rem 1rem', marginTop: 6 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Comparison result
      </div>

      {/* Events */}
      <DiffLine label="Events" both={events.both}
        localOnly={events.localOnly} serverOnly={events.serverOnly} />
      {/* Players */}
      <DiffLine label="Players" both={players.both}
        localOnly={players.localOnly} serverOnly={players.serverOnly} />
      {/* Matches */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4, fontSize: '0.8rem' }}>
        <span style={{ fontWeight: 600, color: '#374151', minWidth: 60 }}>Matches</span>
        <span style={{ color: '#6b7280' }}>{matches.local} local · {matches.server} on server</span>
      </div>

      {!canMerge && (
        <p style={{ fontSize: '0.8rem', color: '#059669', margin: '8px 0 4px' }}>
          ✓ No unique additions on either side — data is structurally identical.
        </p>
      )}

      {canMerge && (
        <div style={{ marginTop: 10, padding: '0.6rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: '0.78rem', color: '#92400e' }}>
          <strong>Merge</strong> will combine both sides — records only on local stay, records only on server are added locally, and conflicts use the newer version.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {canMerge && (
          <button onClick={onMerge} disabled={isBusy || licenseExpired}
            style={{ padding: '5px 12px', fontSize: '0.78rem', background: '#0D9488', color: 'white', border: 'none', borderRadius: 6, cursor: isBusy ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
            {isBusy ? '…' : '⇄ Merge'}
          </button>
        )}
        <button onClick={onPullOverwrite} disabled={isBusy || licenseExpired}
          style={{ padding: '5px 12px', fontSize: '0.78rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: isBusy ? 'not-allowed' : 'pointer' }}>
          ↓ Pull (overwrite local)
        </button>
        <button onClick={onPushOverwrite} disabled={isBusy}
          style={{ padding: '5px 12px', fontSize: '0.78rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: isBusy ? 'not-allowed' : 'pointer' }}>
          ↑ Push (overwrite server)
        </button>
      </div>
    </div>
  );
}

function DiffLine({ label, both, localOnly, serverOnly }) {
  return (
    <div style={{ marginBottom: 6, fontSize: '0.8rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontWeight: 600, color: '#374151', minWidth: 60 }}>{label}</span>
        {both > 0 && <span style={{ color: '#6b7280' }}>{both} shared</span>}
        {localOnly.length > 0 && (
          <span style={{ color: '#b45309', background: '#fffbeb', padding: '1px 6px', borderRadius: 4 }}>
            +{localOnly.length} local only
          </span>
        )}
        {serverOnly.length > 0 && (
          <span style={{ color: '#1d4ed8', background: '#eff6ff', padding: '1px 6px', borderRadius: 4 }}>
            +{serverOnly.length} server only
          </span>
        )}
        {localOnly.length === 0 && serverOnly.length === 0 && both === 0 && (
          <span style={{ color: '#9ca3af' }}>none</span>
        )}
      </div>
      {localOnly.length > 0 && (
        <div style={{ color: '#b45309', fontSize: '0.72rem', marginLeft: 66, marginTop: 1 }}>
          Local: {localOnly.slice(0, 5).join(', ')}{localOnly.length > 5 ? ` +${localOnly.length - 5} more` : ''}
        </div>
      )}
      {serverOnly.length > 0 && (
        <div style={{ color: '#1d4ed8', fontSize: '0.72rem', marginLeft: 66, marginTop: 1 }}>
          Server: {serverOnly.slice(0, 5).join(', ')}{serverOnly.length > 5 ? ` +${serverOnly.length - 5} more` : ''}
        </div>
      )}
    </div>
  );
}

// ── Tournament row ────────────────────────────────────────────────────────────

function TournamentRow({ t, onPull, onPush, onCheck, onCompare, onMerge, syncStatus, diffStatus, activeTournamentId, licenseExpired }) {
  const isBusy = activeTournamentId === t.id;
  const syncedAt = t.synced_at ? new Date(t.synced_at) : null;
  const neverSynced = !t.sync_version;
  const checkedStatus = syncStatus?.status;

  const pullDisabled = isBusy || licenseExpired || neverSynced
    || checkedStatus === 'up_to_date' || checkedStatus === 'not_on_server';
  const pushDisabled = isBusy || checkedStatus === 'up_to_date';
  const showCompare  = !neverSynced && checkedStatus && checkedStatus !== 'up_to_date' && checkedStatus !== 'not_on_server' && checkedStatus !== 'never_synced';

  const statusConfig = {
    up_to_date:   { label: '✓ Up to date',        color: '#059669', bg: '#f0fdf4' },
    local_ahead:  { label: '↑ Push recommended',  color: '#b45309', bg: '#fffbeb' },
    server_ahead: { label: '↓ Pull recommended',  color: '#1d4ed8', bg: '#eff6ff' },
    not_on_server:{ label: '⬆ Not on server yet', color: '#6b7280', bg: '#f9fafb' },
    never_synced: { label: '⬆ Never synced',      color: '#6b7280', bg: '#f9fafb' },
  };

  let syncBadge = null;
  if (syncStatus?.loading) {
    syncBadge = <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Checking…</span>;
  } else if (syncStatus?.error) {
    syncBadge = <span style={{ fontSize: '0.7rem', color: '#dc2626' }}>{syncStatus.error}</span>;
  } else if (syncStatus?.status) {
    const cfg = statusConfig[syncStatus.status] || {};
    syncBadge = (
      <div>
        <span style={{ fontSize: '0.7rem', color: cfg.color, background: cfg.bg, padding: '2px 7px', borderRadius: 99 }}>
          {cfg.label}
        </span>
        {syncStatus.local?.synced_at && (
          <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>
            Last sync: {fmtDate(syncStatus.local.synced_at)}
          </div>
        )}
        {syncStatus.server?.sync_version != null && (
          <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
            Local v{syncStatus.local?.sync_version || 0} · Server v{syncStatus.server?.sync_version || 0}
          </div>
        )}
      </div>
    );
  } else if (syncedAt) {
    syncBadge = <span style={{ fontSize: '0.7rem', color: '#059669', background: '#f0fdf4', padding: '2px 7px', borderRadius: 99 }}>Synced {fmtDate(syncedAt)}</span>;
  } else if (!t.sync_version) {
    syncBadge = <span style={{ fontSize: '0.7rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 7px', borderRadius: 99 }}>Local only</span>;
  }

  const hasDiff = diffStatus?.data;

  return (
    <>
      <tr>
        <td style={{ padding: '10px 8px', fontSize: '0.875rem', fontWeight: 500 }}>{t.name}</td>
        <td style={{ padding: '10px 8px' }}>{syncBadge}</td>
        <td style={{ padding: '10px 8px', fontSize: '0.8rem', color: '#6b7280' }}>{t.sport_type || '—'}</td>
        <td style={{ padding: '10px 8px', textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={() => onCheck(t.id)} disabled={isBusy || syncStatus?.loading}
              style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, cursor: (isBusy || syncStatus?.loading) ? 'not-allowed' : 'pointer' }}>
              Check
            </button>
            {showCompare && (
              <button onClick={() => onCompare(t.id)} disabled={isBusy || diffStatus?.loading}
                style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: 6, cursor: isBusy ? 'not-allowed' : 'pointer' }}>
                {diffStatus?.loading ? '…' : hasDiff ? '↺ Diff' : 'Compare'}
              </button>
            )}
            {!hasDiff && (
              <>
                <button onClick={() => onPull(t.id)} disabled={pullDisabled}
                  title={licenseExpired ? 'Pull disabled — license expired' : neverSynced ? 'Push first to put this tournament on the server' : undefined}
                  style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: pullDisabled ? 'not-allowed' : 'pointer', opacity: pullDisabled ? 0.4 : 1 }}>
                  {isBusy ? '…' : 'Pull'}
                </button>
                <button onClick={() => onPush(t.id)} disabled={pushDisabled}
                  style={{ padding: '4px 10px', fontSize: '0.78rem', background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', borderRadius: 6, cursor: pushDisabled ? 'not-allowed' : 'pointer', opacity: pushDisabled ? 0.4 : 1 }}>
                  {isBusy ? '…' : 'Push'}
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {hasDiff && (
        <tr>
          <td colSpan={4} style={{ padding: '0 8px 10px' }}>
            <DiffPanel
              diff={diffStatus.data}
              tournamentId={t.id}
              isBusy={isBusy}
              licenseExpired={licenseExpired}
              onMerge={() => onMerge(t.id)}
              onPullOverwrite={() => onPull(t.id, true)}
              onPushOverwrite={() => onPush(t.id, true)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SyncManager() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [syncStatuses, setSyncStatuses] = useState({});
  const [diffStatuses, setDiffStatuses] = useState({});   // { [id]: { loading, data, error } }
  const [pullingAll, setPullingAll] = useState(false);
  const [centralTournaments, setCentralTournaments] = useState(null);
  const [centralLoading, setCentralLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, detail, confirmLabel, danger, onConfirm }
  const { syncStatus } = useSyncStatus();
  const { licenseExpired } = useAuth();

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/tournaments');
      setTournaments(res.data || []);
    } catch {
      setErrorMsg('Failed to load local tournaments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!window.electronAPI) return;
    window.electronAPI.onSyncConflict((data) => {
      setErrorMsg(`Sync conflict on tournament — push was rejected. Try merging instead.`);
      setActiveTournamentId(null);
    });
    return () => window.electronAPI.removeSyncListeners();
  }, []);

  useEffect(() => {
    if (syncStatus.status === 'pulled' || syncStatus.status === 'pushed' || syncStatus.status === 'merged') {
      const labels = { pulled: 'Pull', pushed: 'Push', merged: 'Merge' };
      setSuccessMsg(`${labels[syncStatus.status]} complete`);
      setActiveTournamentId(null);
      // Clear diff after a successful sync
      if (syncStatus.tournamentId) {
        setDiffStatuses(prev => ({ ...prev, [syncStatus.tournamentId]: undefined }));
        setSyncStatuses(prev => ({ ...prev, [syncStatus.tournamentId]: undefined }));
      }
      load();
      setTimeout(() => setSuccessMsg(''), 3000);
    }
    if (syncStatus.status === 'error') {
      setErrorMsg(syncStatus.error || 'Sync failed');
      setActiveTournamentId(null);
    }
  }, [syncStatus, load]);

  // ── Pull (with optional overwrite confirmation) ───────────────────────────
  const executePull = async (tournamentId) => {
    setActiveTournamentId(tournamentId);
    setErrorMsg('');
    setSuccessMsg('');
    setDiffStatuses(prev => ({ ...prev, [tournamentId]: undefined }));
    try {
      await window.electronAPI.triggerSync('pull', tournamentId);
      setSuccessMsg('Pull complete — tournament updated from central server.');
      setActiveTournamentId(null);
      load();
    } catch (err) {
      const msg = (err.message || 'Pull failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setErrorMsg(msg);
      setActiveTournamentId(null);
    }
  };

  const handlePull = (tournamentId, forceOverwrite = false) => {
    const t = tournaments.find(x => x.id === tournamentId);
    const localCounts = syncStatuses[tournamentId]?.local?.counts;
    const serverCounts = syncStatuses[tournamentId]?.server?.counts;
    const hasLocalData = localCounts
      ? (localCounts.events > 0 || localCounts.players > 0)
      : t?.sync_version > 0;

    if (hasLocalData && !forceOverwrite) {
      const localDetail = localCounts
        ? `Local: ${localCounts.events} events, ${localCounts.players} players, ${localCounts.matches} matches`
        : 'This tournament has local data.';
      const serverDetail = serverCounts
        ? `Server: ${serverCounts.events} events, ${serverCounts.players} players, ${serverCounts.matches} matches`
        : '';
      setConfirmAction({
        title: 'Overwrite local data?',
        message: `Pulling will replace all local data for "${t?.name}" with the central server version. Any local-only changes will be permanently lost.`,
        detail: [localDetail, serverDetail].filter(Boolean).join('\n'),
        confirmLabel: 'Yes, overwrite local',
        danger: true,
        onConfirm: () => { setConfirmAction(null); executePull(tournamentId); },
      });
    } else {
      executePull(tournamentId);
    }
  };

  // ── Push (with optional overwrite confirmation) ───────────────────────────
  const executePush = async (tournamentId) => {
    setActiveTournamentId(tournamentId);
    setErrorMsg('');
    setSuccessMsg('');
    setDiffStatuses(prev => ({ ...prev, [tournamentId]: undefined }));
    try {
      await window.electronAPI.triggerSync('push', tournamentId);
      setSuccessMsg('Push complete — tournament uploaded to central server.');
      setActiveTournamentId(null);
      load();
    } catch (err) {
      const msg = (err.message || 'Push failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setErrorMsg(msg);
      setActiveTournamentId(null);
    }
  };

  const handlePush = (tournamentId, forceOverwrite = false) => {
    const t = tournaments.find(x => x.id === tournamentId);
    const serverCounts = syncStatuses[tournamentId]?.server?.counts;
    const hasServerData = serverCounts
      ? (serverCounts.events > 0 || serverCounts.players > 0)
      : t?.sync_version > 0;

    if (hasServerData && !forceOverwrite) {
      const serverDetail = serverCounts
        ? `Server: ${serverCounts.events} events, ${serverCounts.players} players, ${serverCounts.matches} matches`
        : 'The server already has data for this tournament.';
      setConfirmAction({
        title: 'Overwrite server data?',
        message: `Pushing will replace all data on the central server for "${t?.name}" with your local version. Any server-only changes will be permanently lost.`,
        detail: serverDetail,
        confirmLabel: 'Yes, overwrite server',
        danger: true,
        onConfirm: () => { setConfirmAction(null); executePush(tournamentId); },
      });
    } else {
      executePush(tournamentId);
    }
  };

  // ── Check ─────────────────────────────────────────────────────────────────
  const handleCheck = async (tournamentId) => {
    setSyncStatuses(prev => ({ ...prev, [tournamentId]: { loading: true } }));
    setDiffStatuses(prev => ({ ...prev, [tournamentId]: undefined }));
    try {
      const result = await window.electronAPI.checkTournamentSyncStatus(tournamentId);
      setSyncStatuses(prev => ({ ...prev, [tournamentId]: { ...result, loading: false } }));
    } catch (err) {
      const msg = (err.message || 'Check failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setSyncStatuses(prev => ({ ...prev, [tournamentId]: { error: msg, loading: false } }));
    }
  };

  // ── Compare ───────────────────────────────────────────────────────────────
  const handleCompare = async (tournamentId) => {
    setDiffStatuses(prev => ({ ...prev, [tournamentId]: { loading: true } }));
    try {
      const result = await window.electronAPI.compareWithCentral(tournamentId);
      setDiffStatuses(prev => ({ ...prev, [tournamentId]: { loading: false, data: result } }));
    } catch (err) {
      const msg = (err.message || 'Compare failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setDiffStatuses(prev => ({ ...prev, [tournamentId]: { loading: false, error: msg } }));
      setErrorMsg(msg);
    }
  };

  // ── Merge ─────────────────────────────────────────────────────────────────
  const handleMerge = (tournamentId) => {
    const t = tournaments.find(x => x.id === tournamentId);
    const diff = diffStatuses[tournamentId]?.data;
    const localOnly  = (diff?.events?.localOnly?.length  || 0) + (diff?.players?.localOnly?.length  || 0);
    const serverOnly = (diff?.events?.serverOnly?.length || 0) + (diff?.players?.serverOnly?.length || 0);
    setConfirmAction({
      title: 'Merge local + server?',
      message: `This will combine both sides for "${t?.name}". Records unique to local stay, records unique to the server are added locally, and for any conflicts the newer version wins.`,
      detail: `${localOnly} local-only record(s) · ${serverOnly} server-only record(s) will be merged.`,
      confirmLabel: 'Merge',
      danger: false,
      onConfirm: () => { setConfirmAction(null); executeMerge(tournamentId); },
    });
  };

  const executeMerge = async (tournamentId) => {
    setActiveTournamentId(tournamentId);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      await window.electronAPI.mergeSyncTournament(tournamentId);
      setSuccessMsg('Merge complete — local and server data combined.');
      setActiveTournamentId(null);
      setDiffStatuses(prev => ({ ...prev, [tournamentId]: undefined }));
      setSyncStatuses(prev => ({ ...prev, [tournamentId]: undefined }));
      load();
    } catch (err) {
      const msg = (err.message || 'Merge failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setErrorMsg(msg);
      setActiveTournamentId(null);
    }
  };

  // ── Pull All ──────────────────────────────────────────────────────────────
  const handlePullAll = async () => {
    setPullingAll(true);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const result = await window.electronAPI.pullAllFromCentral();
      const msg = result.success.length > 0
        ? `Pulled ${result.success.length} tournament(s) from central server.`
        : 'No tournaments to pull.';
      const failMsg = result.failed.length > 0
        ? ` ${result.failed.length} failed: ${result.failed.map(f => f.name).join(', ')}`
        : '';
      setSuccessMsg(msg + failMsg);
      load();
    } catch (err) {
      const msg = (err.message || 'Pull all failed').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setErrorMsg(msg);
    } finally {
      setPullingAll(false);
    }
  };

  const handleFetchCentral = async () => {
    setCentralLoading(true);
    setCentralTournaments(null);
    setErrorMsg('');
    try {
      const list = await window.electronAPI.fetchCentralTournaments();
      setCentralTournaments(list || []);
    } catch (err) {
      const msg = (err.message || 'Failed to fetch central tournaments').replace(/^Error invoking remote method '[^']+': Error:\s*/i, '');
      setErrorMsg(msg);
      setCentralTournaments([]);
    } finally {
      setCentralLoading(false);
    }
  };

  if (!window.electronAPI) {
    return (
      <div style={{ padding: '4rem 1rem', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🖥️</div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151' }}>Desktop only</h2>
        <p style={{ fontSize: '0.875rem' }}>
          Sync is only available in the TournNext desktop app.<br />
          The web app is the central server — no sync needed here.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 1rem', maxWidth: 860, margin: '0 auto' }}>
      {confirmAction && (
        <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Sync Manager</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: 4 }}>
            Pull from or push to the central server
          </p>
        </div>
        <button onClick={handlePullAll} disabled={pullingAll || licenseExpired}
          title={licenseExpired ? 'Pull disabled — license expired' : undefined}
          style={{ padding: '8px 18px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, fontWeight: 600, cursor: (pullingAll || licenseExpired) ? 'not-allowed' : 'pointer', fontSize: '0.875rem', opacity: licenseExpired ? 0.5 : 1 }}>
          {pullingAll ? 'Pulling…' : '↓ Pull All'}
        </button>
      </div>

      {licenseExpired && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span>⚠️</span>
          <span>Your license has expired. <strong>Push</strong> is available to preserve your data. Pull and Merge are disabled until your license is renewed.</span>
        </div>
      )}

      {errorMsg && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {errorMsg}
          <button onClick={() => setErrorMsg('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button>
        </div>
      )}

      {successMsg && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {successMsg}
        </div>
      )}

      {/* Central Server section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#374151' }}>Central Server</h2>
          <button onClick={handleFetchCentral} disabled={centralLoading}
            style={{ padding: '4px 12px', fontSize: '0.78rem', background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, cursor: centralLoading ? 'not-allowed' : 'pointer' }}>
            {centralLoading ? 'Fetching…' : centralTournaments === null ? '↺ Compare with central' : '↺ Refresh'}
          </button>
        </div>

        {centralTournaments !== null && (() => {
          const localIds = new Set(tournaments.map(t => String(t.id)));
          const centralOnly = centralTournaments.filter(t => !localIds.has(String(t.id)));
          if (centralOnly.length === 0) {
            return <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0 }}>All central tournaments are already on this device.</p>;
          }
          return (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ padding: '8px 12px', background: '#dbeafe', fontSize: '0.78rem', fontWeight: 600, color: '#1d4ed8' }}>
                {centralOnly.length} tournament{centralOnly.length !== 1 ? 's' : ''} on central not yet downloaded
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {centralOnly.map((t, i) => (
                    <tr key={t.id} style={{ borderTop: i > 0 ? '1px solid #bfdbfe' : 'none' }}>
                      <td style={{ padding: '8px 12px', fontSize: '0.875rem', fontWeight: 500, color: '#1e40af' }}>{t.name}</td>
                      <td style={{ padding: '8px 12px', fontSize: '0.8rem', color: '#3b82f6' }}>{t.sport_type || '—'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <button onClick={() => executePull(t.id)} disabled={activeTournamentId === t.id || licenseExpired}
                          style={{ padding: '4px 12px', fontSize: '0.78rem', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 6, cursor: (activeTournamentId === t.id || licenseExpired) ? 'not-allowed' : 'pointer', opacity: licenseExpired ? 0.5 : 1 }}>
                          {activeTournamentId === t.id ? '…' : '↓ Pull'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Local tournaments table */}
      {loading ? (
        <p style={{ color: '#9ca3af', textAlign: 'center', marginTop: '3rem' }}>Loading tournaments…</p>
      ) : tournaments.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '4rem' }}>
          <p style={{ fontSize: '1rem' }}>No tournaments yet.</p>
          <p style={{ fontSize: '0.875rem' }}>Create a tournament or pull one from the central server.</p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ padding: '10px 8px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textAlign: 'left' }}>Tournament</th>
                <th style={{ padding: '10px 8px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textAlign: 'left' }}>Sync Status</th>
                <th style={{ padding: '10px 8px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textAlign: 'left' }}>Sport</th>
                <th style={{ padding: '10px 8px', fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tournaments.map((t, i) => (
                <React.Fragment key={t.id}>
                  {i > 0 && <tr><td colSpan={4} style={{ height: 1, background: '#f9fafb', padding: 0 }} /></tr>}
                  <TournamentRow
                    t={t}
                    onPull={handlePull}
                    onPush={handlePush}
                    onCheck={handleCheck}
                    onCompare={handleCompare}
                    onMerge={handleMerge}
                    syncStatus={syncStatuses[t.id]}
                    diffStatus={diffStatuses[t.id]}
                    activeTournamentId={activeTournamentId}
                    licenseExpired={licenseExpired}
                  />
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
